import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";

export interface LinkPreview {
  url: string;
  title: string;
  description?: string;
  image?: string;
  site_name?: string;
}

// Module-level cache keyed by thought id. Enrichment runs at most once per
// card ever: the link-preview edge function stores the result on the row, so
// future sessions render straight from thoughts.link_preview.
const cache = new Map<string, LinkPreview | null>();
const inFlight = new Map<string, Promise<LinkPreview | null>>();

// A library full of not-yet-enriched links would otherwise fire one function
// call per card at once; cap how many run concurrently.
const MAX_CONCURRENT = 4;
let active = 0;
const waiters: Array<() => void> = [];
async function acquireSlot(): Promise<void> {
  if (active >= MAX_CONCURRENT) {
    await new Promise<void>((resolve) => waiters.push(resolve));
  }
  active++;
}
function releaseSlot(): void {
  active--;
  waiters.shift()?.();
}

async function fetchPreview(thoughtId: string): Promise<LinkPreview | null> {
  // Optimistic inserts carry a temp- id until the DB row exists.
  if (thoughtId.startsWith("temp-")) return null;
  if (cache.has(thoughtId)) return cache.get(thoughtId)!;
  if (inFlight.has(thoughtId)) return inFlight.get(thoughtId)!;

  const promise = (async () => {
    await acquireSlot();
    try {
      const { data, error } = await supabase.functions.invoke("link-preview", {
        body: { thought_id: thoughtId },
      });
      if (error || !data?.preview) throw error ?? new Error("no preview");
      // A throttled/unparsed response is a domain-only placeholder — don't
      // cache it as the real thing beyond this session.
      const preview = data.preview as LinkPreview;
      cache.set(thoughtId, preview);
      return preview;
    } catch {
      cache.set(thoughtId, null);
      return null;
    } finally {
      releaseSlot();
      inFlight.delete(thoughtId);
    }
  })();

  inFlight.set(thoughtId, promise);
  return promise;
}

/**
 * Server-side link enrichment for a link-only thought that doesn't yet have a
 * stored preview. Pass null when the thought already carries `link_preview`
 * (render that directly) or isn't a bare link.
 */
export function useLinkPreview(thoughtId: string | null | undefined): LinkPreview | null {
  const [preview, setPreview] = useState<LinkPreview | null>(() =>
    thoughtId ? (cache.get(thoughtId) ?? null) : null
  );

  useEffect(() => {
    if (!thoughtId) return;
    if (cache.has(thoughtId)) {
      setPreview(cache.get(thoughtId) ?? null);
      return;
    }
    let cancelled = false;
    fetchPreview(thoughtId).then((p) => {
      if (!cancelled) setPreview(p);
    });
    return () => {
      cancelled = true;
    };
  }, [thoughtId]);

  return preview;
}

export { fetchPreview };
